import * as React from 'react'
import { useNavigate } from 'react-router-dom'

import { Box, Button, Title } from '@factorialco/deprecated-design-system'
import { Features } from 'generated/featureDefinitions'
import { FetchTimeoffDataDocument } from 'generated/resources/graphql'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { useQuery } from 'modules/core/hooks/useQuery'
import i18n from 'modules/core/lib/i18n'

import ApprovalsBlock from 'modules/approvals/components/ApprovalsConfigurationBlock'
import { ResponsivePage } from 'modules/layout/components/ResponsivePage'
import BlockedPeriodsSettingsBlock from 'modules/timeoff/components/BlockedPeriodsSettings/Block'
import LeaveTypesBlock from 'modules/timeoff/components/LeaveTypeSettings/LeaveTypesBlock'
import TimeoffPolicies from 'modules/timeoff/components/TimeoffPolicies'

const LocationsContainer = () => {
  const { company } = useCurrentSession()
  const { navigation } = useNavigation()
  const navigate = useNavigate()

  const { data, loading, error } = useQuery(FetchTimeoffDataDocument, {
    variables: {
      companyIds: [company.id],
      featureIds: [Features.timeoff_management, Features.timeoff_blocked_periods],
      fetchBlockedPeriods: true,
      fetchLeaveTypes: true,
      fetchPolicies: true,
    },
  })
  const leaveTypes = data?.timeoff.leaveTypes || []
  const policies = data?.timeoff.policiesConnection?.nodes || []
  const canManageTimeOff = data?.timeoff._policies.companyManageTimeoff.can ?? false
  const timeoffPoliciesTotal = data?.timeoff.policiesConnection?.totalCount
  const blockedPeriodsPolicies = data?.timeoff.blockedPeriodsConnection?.nodes || []
  const blockedPeriodsPoliciesTotal = data?.timeoff.blockedPeriodsConnection?.totalCount
  const blockedPeriodsEnabled =
    data?.companies.featureAvailabilitiesConnection.nodes.find(
      (b_p) => b_p.id === Features.timeoff_blocked_periods
    )?.available || false

  const onClickNewPolicy = () => {
    navigate(navigation.settings.timeoffPolicies.new.fullPath())
  }

  return (
    <ResponsivePage.Body loading={loading} error={error} showHeader={false}>
      <Box>
        <Box gap="s24">
          <Title
            title={i18n.t('settings.timeoff_policies.list.title')}
            description={i18n.t('settings.timeoff_policies.list.description')}
            illustration="calendar-clock"
            variant="500"
          ></Title>
          <Button onClick={onClickNewPolicy} iconName="add">
            {i18n.t('settings.timeoff_policies.add')}
          </Button>
        </Box>
        <Box paddingY="s24" extra={{ 'data-intercom-target': 'timeoff-policies-list' }}>
          <TimeoffPolicies timeoffPolicies={policies} totalCount={timeoffPoliciesTotal} />
        </Box>
      </Box>
      <Box paddingY="s24">
        <ApprovalsBlock
          category="timeoff"
          title={i18n.t('settings.time_off.approvals.title')}
          description={i18n.t('settings.time_off.approvals.description')}
          helpCenterUrlName="timeoff_approvals"
          noPadding
        />
      </Box>
      {blockedPeriodsEnabled && (
        <Box paddingY="s24">
          <BlockedPeriodsSettingsBlock
            title={i18n.t('settings.time_off.blocked_periods.title')}
            description={i18n.t('settings.time_off.blocked_periods.description')}
            helpCenterUrlName="timeoff_blocked_periods"
            data={blockedPeriodsPolicies}
            totalCount={blockedPeriodsPoliciesTotal}
            defaultLeaveTypeId={leaveTypes[0]?.id || 0}
            canManageTimeOff={canManageTimeOff}
          />
        </Box>
      )}
      <Box paddingY="s24">
        <LeaveTypesBlock loading={loading} data={leaveTypes} />
      </Box>
    </ResponsivePage.Body>
  )
}

export default LocationsContainer
